import {useCallback, useEffect, useMemo} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {DocumentUpdateSpec, EventConstants} from 'constants/EventConstants';
import DocumentActions from 'notebook/documents/DocumentActions';
import DocumentStore from 'notebook/documents/DocumentStore';
import DocumentUtil from 'notebook/documents/DocumentUtil';
import {getDocumentSaveState} from 'notebook/documents/useDocumentSaveState';
import {EntryHistoryState} from 'notebook/ELNConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import DocumentUpdatesManager from 'notebook/realtime/DocumentUpdatesManager';
// @ts-expect-error
import HistoryManager from 'notebook/store/HistoryManager';
import {StructuredTableMapping} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {Note} from 'notebook/util/NoteUtilConstants';
import DocumentEditorLockUtil from 'realtime/util/DocumentEditorLockUtil';

export interface UseDocument {
  getIdFromCid: (cid: string) => string | number | undefined;
  historyManager: HistoryManager;
  isLatest: boolean;
  note?: Note;
  isDirty: boolean;
  parentType: string;
  registerObjects: (cidIdMap: unknown) => void;
  updateNote: (
    note: Note,
    options?: {
      performTableEvaluation?: boolean;
      updatedItemPartIndexes?: Array<number>;
      canIncrementalSave?: boolean;
    },
    structuredTableMappings?: Array<StructuredTableMapping>,
    relatedDocuments?: {[documentId: string]: Note}
  ) => void;
  updateTitle: (title: string) => void;
  restoreStateAndSelection: (state: EntryHistoryState, selectionData: SelectionRestoreData) => void;
}

/** The following event types when performed on the DocumentStore will cause the onChange callback
 * to be triggered.
 */
const EVENTS_THAT_PROMPT_DOCUMENT_CHANGE: Array<string> = [
  EventConstants.DOCUMENT_UPDATE,
  EventConstants.DOCUMENT_SERVER_SIDE_UPDATE,
  EventConstants.STRUCTURED_TABLE_INVOKE_MAPPINGS,
];

export default function useDocument(
  documentId: string,
  disableAutoSaver?: boolean,
  onChange?: (wasServerUpdate: boolean, changedDocumentId: string) => void,
  customSaveHandler?: () => Promise<boolean>,
  manageRealtimeUpdates: boolean = false,
  getRelatedDocumentIds?: () => Array<string>
): UseDocument {
  const historyManager = useMemo(() => new HistoryManager(), []);
  const documentUpdatesManager = useMemo(
    () => (manageRealtimeUpdates ? new DocumentUpdatesManager() : null),
    [manageRealtimeUpdates]
  );

  const parentType = 'Note';

  const {isLatest, latestVersionId, isDirty} = getDocumentSaveState(documentId);

  const registerObjects: (cidIdMap: unknown) => void = useCallback(
    (cidIdMap: CidIdMap) => {
      DocumentActions.registerObjects(documentId, cidIdMap);
    },
    [documentId]
  );

  const getIdFromCid: (cid: string) => string | number | undefined = useCallback(
    (cid: string) => DocumentStore.getIdFromCid(documentId, cid),
    [documentId]
  );

  const updateNote = useCallback(
    (
      note: Note,
      options?: {
        performTableEvaluation?: boolean;
        updatedItemPartIndexes?: Array<number>;
        canIncrementalSave?: boolean;
      },
      structuredTableMappings?: Array<StructuredTableMapping>,
      updatedRelatedDocuments?: {[documentId: string]: Note}
    ): void => {
      if (updatedRelatedDocuments && BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS) {
        const relatedDocumentSpecs: Array<DocumentUpdateSpec> = Object.entries(updatedRelatedDocuments).map(
          ([documentId, relatedNote]) => ({
            id: documentId,
            spec: DocumentUtil.mergeEntryVersionUpdateWithNote(DocumentUtil.toEntryNote(relatedNote)),
          })
        );
        DocumentActions.updateDocumentGroup(
          {
            id: documentId,
            spec: DocumentUtil.mergeEntryVersionUpdateWithNote(DocumentUtil.toEntryNote(note)),
          },
          relatedDocumentSpecs,
          {
            structuredTableMappings,
            ...options,
          }
        );
      } else {
        DocumentActions.updateNote(documentId, DocumentUtil.toEntryNote(note), {
          structuredTableMappings,
          ...options,
        });
      }
    },
    [documentId]
  );

  const updateTitle: (title: string) => void = useCallback(
    (name: string) => {
      DocumentActions.setName(documentId, name);
    },
    [documentId]
  );

  const restoreStateAndSelection = useCallback(
    (state: EntryHistoryState, selectionData: SelectionRestoreData): void => {
      DocumentActions.revert(
        documentId,
        {
          name: {$set: state.title},
          ...DocumentUtil.entryVersionUpdate({$merge: {note: state.note}}),
        },
        selectionData
      );
    },
    [documentId]
  );
  /**
   * Open the document, fetching it if needed, and set a custom save handler if provided
   */
  useEffect(() => {
    const openDocument = async (): Promise<void> => {
      const document = DocumentStore.getDocument(documentId);
      historyManager.reset();
      if (getRelatedDocumentIds) {
        DocumentStore.setRelatedDocumentIdGetter(documentId, getRelatedDocumentIds);
      }
      if (!document) {
        await DocumentActions.open(documentId, {
          disableAutoSaver,
          relatedDocumentIdGetter: BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS
            ? getRelatedDocumentIds
            : undefined,
        });
        if (customSaveHandler) {
          DocumentStore.setCustomSaveHandler(documentId, customSaveHandler);
        }
      } else if (document.isStale) {
        await DocumentActions.refresh(documentId);
      }
    };
    openDocument();
  }, [documentId, disableAutoSaver, customSaveHandler, getRelatedDocumentIds, historyManager]);

  /**
   * Add a listener for document updates to trigger the onChange callback
   */
  useEffect(() => {
    const callback = onChange
      ? (context: {id?: string; actionType: string}): void => {
          const {id, actionType} = context;
          const relatedDocumentIds = BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS
            ? DocumentStore.getRelatedDocumentIds(documentId)
            : [];
          if (
            (documentId === id || (id != null && relatedDocumentIds?.includes(id))) &&
            DocumentStore.inStore(documentId) &&
            EVENTS_THAT_PROMPT_DOCUMENT_CHANGE.includes(actionType)
          ) {
            onChange(actionType === EventConstants.DOCUMENT_SERVER_SIDE_UPDATE, id);
          }
        }
      : null;
    if (callback) {
      DocumentStore.addChangeListener(callback);
    }
    return () => {
      if (callback) {
        DocumentStore.removeChangeListener(callback);
      }
    };
  }, [documentId, onChange]);

  /**
   * Add a listener for server side updates to trigger a reload of the history manager
   */
  useEffect(() => {
    const onServerSideUpdate = (context: {id?: string; actionType: string}): void => {
      const {id, actionType} = context;
      if (
        documentId === id &&
        DocumentStore.inStore(documentId) &&
        actionType === EventConstants.DOCUMENT_SERVER_SIDE_UPDATE
      ) {
        historyManager.reset();
      }
    };
    DocumentStore.addChangeListener(onServerSideUpdate);
    return () => {
      DocumentStore.removeChangeListener(onServerSideUpdate);
    };
  }, [documentId, historyManager]);

  useEffect(() => {
    documentUpdatesManager?.changeActiveDocument(documentId);
    return () => {
      documentUpdatesManager?.markInactive();
    };
  }, [documentId, documentUpdatesManager]);

  /**
   * Load the latest version if it is different from the current version
   */
  useEffect(() => {
    if (
      latestVersionId !== undefined &&
      (!DocumentStore.getDocument(documentId) ||
        (!DocumentStore.hasUnsavedChanges(documentId) &&
          DocumentStore.getVersionState(documentId).openVersionId !== latestVersionId))
    ) {
      DocumentActions.loadVersion(documentId, latestVersionId);
      historyManager.reset();
    }

    // Release lock on unmount
    return () => {
      DocumentEditorLockUtil.releaseLock(documentId);
    };
    // We don't want to reload every time the latest version updates, only when we change to a different doc
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  return {
    getIdFromCid,
    historyManager,
    isLatest,
    isDirty,
    parentType,
    registerObjects,
    updateNote,
    updateTitle,
    restoreStateAndSelection,
  };
}
